import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EditPasswordFormData } from '@jwp/ott-common/types/account';
import type { FormErrors } from '@jwp/ott-common/types/form';
import { testId } from '@jwp/ott-common/src/utils/common';

import PasswordField from '../PasswordField/PasswordField';
import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import styles from './EditAccountPassword.module.scss';

type Props = {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  setValue?: (key: keyof EditPasswordFormData, value: string) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string;
  errors: FormErrors<EditPasswordFormData>;
  value: EditPasswordFormData;
  validationError?: boolean;
  submitting: boolean;
  showOldPasswordField?: boolean;
  showResetTokenField?: boolean;
  email?: string;
  onResendEmailClick?: () => void;
  setShowPasswordContent: any;
};

const EditAccountPasswordForm: React.FC<Props> = ({
  onSubmit,
  onChange,
  onBlur,
  showOldPasswordField,
  showResetTokenField,
  value,
  errors,
  submitting,
  validationError,
  email,
  onResendEmailClick,
  setShowPasswordContent,
}: Props) => {
  const { t } = useTranslation(['account', 'user']);

  return (
    <form data-testid={testId('forgot-password-form')} noValidate className={styles.formlayout}>
      {errors.form && (
        <FormFeedback variant="error" visible={!validationError}>
          {errors.form}
        </FormFeedback>
      )}
      {showOldPasswordField && showResetTokenField && (
        <p className={styles.paragraph}>
          {t('user:account.add_password_modal_text', { email: email })}
          <a className={styles.resendLink} onClick={onResendEmailClick}>
            {t('user:account.resend_mail')}
          </a>
        </p>
      )}

      <div className={styles.forgotPasswordForm}>
        <PasswordField
          value={value.oldPassword}
          onChange={onChange}
          onBlur={onBlur}
          label={t('reset.current_password')}
          placeholder={t('reset.current_password_placeholder')}
          error={!!errors.oldPassword}
          name="oldPassword"
          showHelperText={false}
          helperText={errors.oldPassword}
          required
        />
        <PasswordField
          value={value.password}
          onChange={onChange}
          onBlur={onBlur}
          label={t('reset.new_password')}
          placeholder={t('reset.new_password_placeholder')}
          error={!!errors.password}
          name="password"
          helperText={errors.password}
          showHelperText={false}
          required
        />

        <PasswordField
          value={value.passwordConfirmation}
          onChange={onChange}
          onBlur={onBlur}
          label={t('reset.ReEnter_New_Passwor')}
          placeholder={t('reset.confirm_password_placeholder')}
          error={!!errors.passwordConfirmation}
          name="passwordConfirmation"
          helperText={errors.passwordConfirmation}
          showHelperText={false}
          required
        />
      </div>
      <div className={styles.buttonBar}>
        <Button label={t('reset.cancel')} onClick={() => setShowPasswordContent(false)} type="reset" size="large" variant="transparent" />
        <Button label={t('reset.save')} onClick={onSubmit} type="submit" size="large" color="primary" variant="primarySmall" />
      </div>

      {submitting && <LoadingOverlay transparentBackground inline />}
    </form>
  );
};

export default EditAccountPasswordForm;
